import React, { ReactElement, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  BreadcrumbGroup,
  Button,
  Checkbox,
  Container,
  FormField,
  Grid,
  Header,
  InputProps,
  Modal,
  Pagination,
  Select,
  SpaceBetween,
  Table,
  TextContent,
} from '@amzn/awsui-components-react-v3';
import { OptionDefinition } from '@amzn/awsui-components-react-v3/polaris/internal/components/option/interfaces';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useArgoContext } from '@awsscm/argo-core';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { PART_SCAN_COLUMNS } from './tableColumnDefinitions';
import { ContainerValidated, GetContainerResponse, MobilityPart, Part } from './types';
import ValidatingContainerInput from '../../components/ValidatedContainerInput';
import RefScanInput from '../../components/RefScanInput';
import { GET_PARTS } from '../../services/container-management/queries';
import {
  BULK_MOVE_PARTS,
  GET_PARTS_IN_USER_CUSTODY,
  GET_WORKFLOW_IDS,
  MOVE_PART,
} from '../../services/parts-move/queries';
import { MoveContainer, ParentContainersMobility } from '../../services/parts-move/types';

/**
 * Component for Landing Page
 * @component
 * @returns {ReactElement}
 */
const PartsMove = (): ReactElement => {
  const {
    user: { username },
  } = useArgoContext();
  const { t } = useTranslation();
  const breadcrumbs = [
    {
      text: t('breadcrumb.home'),
      href: '/',
    },
    {
      text: t('breadcrumb.inventory-management'),
      href: '/assets-inventory/',
    },
    {
      text: t('breadcrumb.parts-move'),
      href: '/assets-inventory/parts-move',
    },
  ];

  const [sourceType, setSourceType] = React.useState<OptionDefinition>({
    value: 'container',
    label: 'Container',
  });
  const [sourceInputLabel, setSourceInputLabel] = React.useState('Container Tag');
  const [destType, setDestType] = React.useState<OptionDefinition>({
    value: 'container',
    label: 'Container',
  });
  const [destInputLabel, setDestInputLabel] = React.useState('Container Tag');
  const [source, setSource] = React.useState('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [sourceContainer, setSourceContainer] = React.useState<GetContainerResponse>();
  const [sourceMobilityId, setSourceMobilityId] = React.useState(-1);
  const [destMobilityId, setDestMobilityId] = React.useState(-1);
  const [sourceParentMobilityIds, setSourceParentMobilityIds] = React.useState<
    ParentContainersMobility[]
  >([]);
  const [destParentMobilityIds, setDestParentMobilityIds] = React.useState<
    ParentContainersMobility[]
  >([]);
  const [sourceDisabled, setSourceDisabled] = React.useState(false);
  const [dest, setDest] = React.useState('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [destContainer, setDestContainer] = React.useState<GetContainerResponse>();
  const [destDisabled, setDestDisabled] = React.useState(false);
  const [validSource, setValidSource] = React.useState(ContainerValidated.UNCHECKED);
  const [validDest, setValidDest] = React.useState(ContainerValidated.UNCHECKED);
  const [scanningStarted, setScanningStarted] = React.useState(false);
  const [moveAllChecked, setMoveAllChecked] = React.useState(false);
  const [partsCheckboxDisabled, setPartsCheckboxDisabled] = React.useState(true);

  const [partScanDisabled, setPartScanDisabled] = React.useState(true);
  const [partScanButtonDisabled, setPartScanButtonDisabled] = React.useState(true);
  const [bulkPartList, setBulkPartList] = React.useState<MobilityPart[]>([]);

  // this will hold the initial scans of serials and will get updated when the below list changes
  const [serialList, setSerialList] = React.useState<Part[]>([]);
  // this will hold all returns from the api for each serial
  const [returnedSerialList, setReturnedSerialList] = React.useState<Part[]>([]);

  const [isRevertDisabled, setIsRevertDisabled] = React.useState(false);
  const [selectedSerialsString, setSelectedSerialsString] = React.useState('');
  const [containerFieldsDisabled, setContainerFieldsDisabled] = React.useState(false);
  const serialIdScanRef: React.RefObject<InputProps.Ref> = React.useRef(null);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [confirmMoveDisabled, setConfirmMoveDisabled] = React.useState(false);
  const [currentSerial, setCurrentSerial] = React.useState('');
  const [modalVisible, setModalVisible] = React.useState(false);
  const [scanError, setScanError] = React.useState('');
  const [selectedItems, setSelectedItems] = React.useState<Part[]>([]);
  const [errorText, setErrorText] = React.useState('');
  const [bulkMoveDisabled, setBulkMoveDisabled] = React.useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = React.useState<OptionDefinition>({
    value: '',
    label: 'Default',
  });
  const [workflowIds, setWorkflowIds] = React.useState<OptionDefinition[]>([
    {
      value: '',
      label: 'Default',
    },
  ]);
  const { items, paginationProps } = useCollection(serialList, {
    pagination: { pageSize: 10 },
  });

  /**
   * Get parts in container is called upon successfully validating the source container. These values are never used in an API call
   * but are displayed when the user selects "Add all parts" so we can give them a visual of what they are bulk moving
   */
  const [getPartsInContainer, { data: partsInContainerData, loading: partsInContainerLoading }] =
    useLazyQuery(GET_PARTS, {
      variables: { containerId: '' },
      fetchPolicy: 'no-cache',
    });

  const [getPartsInUserCustody, { data: partsInCustodyData }] = useLazyQuery(
    GET_PARTS_IN_USER_CUSTODY,
    {
      variables: { userAlias: '' },
      fetchPolicy: 'no-cache',
    }
  );

  const [getWorkflowIds, { data: workflowIdsData, error: workflowIdsError }] = useLazyQuery(
    GET_WORKFLOW_IDS,
    {
      variables: { sourceType: '', destinationType: '' },
      fetchPolicy: 'no-cache',
    }
  );

  const [movePart] = useMutation(MOVE_PART, {
    fetchPolicy: 'no-cache',
  });

  const [bulkMoveParts] = useMutation(BULK_MOVE_PARTS, {
    fetchPolicy: 'no-cache',
  });

  /**
   * useEffect hook for updating the state with the workflow ids fetched from the API
   */
  useEffect(() => {
    let workflowResponse: string[] = [];
    if (workflowIdsData?.getWorkflows.workflows) {
      workflowResponse = workflowIdsData?.getWorkflows.workflows;
    } else if (workflowIdsError) {
      workflowResponse = [
        'parts_move_check_hdd_into_container',
        'parts_move_check_into_container',
        'parts_move_check_pending_build_into_container',
        'parts_move_check_ssd_into_container',
        'parts_move_check_hdd_out_from_container',
        'parts_move_check_out_from_container',
        'parts_move_check_out_from_container_for_build',
        'parts_move_check_out_from_container_for_repair',
        'parts_move_check_out_from_container_for_transfer',
        'parts_move_check_ssd_out_from_container',
      ];
    }
    const workflowOptions: OptionDefinition[] = [
      {
        value: '',
        label: 'Default',
      },
    ];
    workflowResponse.forEach((workflow: string): void => {
      workflowOptions.push({ value: workflow, label: workflow });
    });
    setWorkflowIds(workflowOptions);
  }, [workflowIdsData, workflowIdsError]);

  /**
   * useEffect hook for changes to the source type
   */
  useEffect(() => {
    // when the source type changes, if they are both the same, change the destination type to the opposite
    if (sourceType.value === 'user_custody') {
      setSourceInputLabel('User custody');
      setSource(username);
      setSourceDisabled(true);
      setValidSource(ContainerValidated.VALIDATED);
    } else {
      setSourceInputLabel('Container Tag');
      setSource('');
      setSourceDisabled(false);
    }
  }, [username, sourceType]);

  /**
   * useEffect hook for changes to the destination type
   */
  useEffect(() => {
    if (destType.value === 'user_custody') {
      setDestInputLabel('User custody');
      setDest(username);
      setDestDisabled(true);
      setValidDest(ContainerValidated.VALIDATED);
    } else {
      setDestInputLabel('Container Tag');
      setDest('');
      setDestDisabled(false);
    }
  }, [username, destType]);

  /**
   * useEffect hook to clear the validation status if the user changes the input
   */
  useEffect(() => {
    if (sourceType.value === 'container') {
      setValidSource(ContainerValidated.UNCHECKED);
    }
  }, [source, sourceType.value]);

  useEffect(() => {
    if (destType.value === 'container') {
      setValidDest(ContainerValidated.UNCHECKED);
    }
  }, [dest, destType.value]);

  /**
   * useEffect hook for enabling and disabling the serial id input when appropriate
   */
  useEffect(() => {
    if (
      (validSource === ContainerValidated.VALIDATED &&
        validDest === ContainerValidated.VALIDATED) ||
      (validSource === ContainerValidated.VALIDATED && destType.value === 'user_custody') ||
      (sourceType.value === 'user_custody' && validDest === ContainerValidated.VALIDATED)
    ) {
      setPartScanButtonDisabled(false);
      setPartsCheckboxDisabled(false);
      const requestSourceType =
        sourceType.value === 'user_custody'
          ? 'user_custody'
          : sourceContainer?.containerDefinition.containerType;
      const requestDestType =
        destType.value === 'user_custody'
          ? 'user_custody'
          : destContainer?.containerDefinition.containerType;
      getWorkflowIds({
        variables: { sourceType: requestSourceType || '', destinationType: requestDestType || '' },
      });
    } else {
      setPartScanButtonDisabled(true);
      setPartsCheckboxDisabled(true);
    }
  }, [
    validSource,
    validDest,
    destType.value,
    sourceType.value,
    sourceContainer,
    destContainer,
    getWorkflowIds,
  ]);

  /**
   * useEffect hook to update table and table info
   */
  useEffect(() => {
    // edit enabled for 1 selected, delete enabled for 1 or more selected
    switch (selectedItems.length) {
      case 0:
        setIsRevertDisabled(true);
        break;
      default:
        setIsRevertDisabled(false);
        break;
    }
    let serialStrings = '';
    selectedItems.forEach((part) => {
      serialStrings = `${serialStrings + part.serialId}\n`;
    });
    setSelectedSerialsString(serialStrings);
  }, [serialList, selectedItems]);

  /**
   * useEffect hook to update the local/pending list of serials when an API result is returned
   */
  useEffect(() => {
    // to maintain the ordering of the user scans and not the order that we get responses from the API
    // loop through the local list and update with any responses from api
    const updatedSerialList: Part[] = [];
    serialList.forEach((localPart) => {
      const updatedPart = returnedSerialList.find(
        (apiPart) => localPart.serialId === apiPart.serialId
      );
      // eslint-disable-next-line no-unused-expressions
      updatedPart ? updatedSerialList.push(updatedPart) : updatedSerialList.push(localPart);
    });
    setSerialList([...updatedSerialList]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [returnedSerialList]);

  /**
   * useEffect hook to get the parts in the source container once it is validated
   */
  useEffect(() => {
    if (validSource === ContainerValidated.VALIDATED) {
      getPartsInContainer({
        variables: { containerId: sourceContainer?.container.containerId || '' },
      });
    }
  }, [getPartsInContainer, source, sourceContainer, validSource]);

  /**
   * useEffect hook to get the parts in tuser custody when source type changes
   */
  useEffect(() => {
    if (sourceType.value === 'user_custody') {
      getPartsInUserCustody({
        variables: { userAlias: source },
      });
    }
  }, [getPartsInUserCustody, source, sourceType.value]);

  /**
   * useEffect hook to update the bulk parts list that is displayed when we receive a response from the API
   */
  useEffect(() => {
    if (partsInCustodyData) {
      setBulkPartList(partsInCustodyData?.getPartsInUserCustody.parts);
    }
  }, [partsInCustodyData]);

  /**
   * useEffect hook to update the bulk parts list that is displayed when we receive a response from the API
   */
  useEffect(() => {
    if (partsInContainerData) {
      setBulkPartList(partsInContainerData?.getParts.parts);
    }
  }, [partsInContainerData, partsInContainerLoading]);

  /**
   * useEffect hook to display an error to the user if the select user_custody source and destination
   */
  useEffect(() => {
    if (sourceType.value === 'user_custody' && destType.value === 'user_custody') {
      setErrorText('Cannot move parts from user custody to user custody.');
    } else {
      setErrorText('');
    }
  }, [sourceType, destType]);

  /**
   * useEffect hook to reset the scan input error once the user starts changing or deletes the input
   */
  useEffect(() => {
    setScanError('');
  }, [currentSerial]);

  /**
   * called when the user clicks the start scanning button, this will disable the source/destination related fields
   * and the scan button, basically puts the app into scanning mode, moves focus to the scan field
   */
  const startScanningProcess = (): void => {
    setScanningStarted(true);
    setPartScanButtonDisabled(true);
    setSourceDisabled(true);
    setDestDisabled(true);
    setPartScanDisabled(false);
    setContainerFieldsDisabled(true);
    setPartsCheckboxDisabled(true);
    // eslint-disable-next-line no-unused-expressions
    serialIdScanRef.current?.focus();
  };

  /**
   * takes the parts which are in the source container and adds them to the scan table, this is nothing but a visual
   * operation so the user can see what is being moved
   * TODO: update some of the table fields that are filled in with test values, we might want to remove some table
   * columns or figure out what we should put there
   */
  const addAllPartsToMove = (): void => {
    if (!moveAllChecked) {
      setSourceDisabled(true);
      setDestDisabled(true);
      setMoveAllChecked(true);
      setScanningStarted(true);
      setPartScanButtonDisabled(true);
      setContainerFieldsDisabled(true);
      const bulkSerialList: Part[] = [];
      bulkPartList.forEach((part) => {
        bulkSerialList.push({
          response: 'pending_bulk_move',
          serialId: part.serialId || part.serial_id || '',
          location: source,
          action: 'None',
        });
      });
      setSerialList(bulkSerialList);
    } else {
      setSourceDisabled(false);
      setDestDisabled(false);
      setMoveAllChecked(false);
      setScanningStarted(false);
      setPartScanButtonDisabled(false);
      setContainerFieldsDisabled(false);
      setSerialList([]);
    }
  };

  /**
   * This is the function to be called when the user has selected the "Add all parts" checkbox, the parts are added to
   * the table and they want to confirm the move.
   */
  const submitBulkPartsMove = (): void => {
    serialList.map((part) => {
      // eslint-disable-next-line no-param-reassign
      part.response = 'loading';
      return null;
    });
    setSerialList([...serialList]);
    const requestSource =
      sourceType.value === 'user_custody' ? source : sourceMobilityId.toString();
    const requestDestination = destType.value === 'user_custody' ? dest : destMobilityId.toString();
    const requestSourceType =
      sourceType.value === 'user_custody'
        ? 'user_custody'
        : sourceContainer?.containerDefinition.containerType;
    const requestDestinationType =
      destType.value === 'user_custody'
        ? 'user_custody'
        : destContainer?.containerDefinition.containerType;
    const parentContainers: MoveContainer[] = [];
    destParentMobilityIds.forEach((container) => {
      parentContainers.push({ id: container.mobilityId, type: container.type });
    });
    bulkMoveParts({
      variables: {
        source: {
          id: requestSource,
          type: requestSourceType,
        },
        destination: {
          id: requestDestination,
          type: requestDestinationType,
        },
        parentContainers,
        login: username,
        workflowId: selectedWorkflow.value,
      },
    }).then((r) => {
      if (r.data.bulkMoveParts.status === 'SUCCESS') {
        serialList.map((part) => {
          // eslint-disable-next-line no-param-reassign
          part.response = 'success';
          // eslint-disable-next-line no-param-reassign
          part.location = dest;
          // eslint-disable-next-line no-param-reassign
          part.action = 'None';
          return null;
        });
        setReturnedSerialList([...serialList]);
        setBulkMoveDisabled(true);
      } else {
        serialList.map((part) => {
          // eslint-disable-next-line no-param-reassign
          part.response = 'error';
          // eslint-disable-next-line no-param-reassign
          part.location = source;
          // eslint-disable-next-line no-param-reassign,prefer-destructuring
          part.action = r.data.bulkMoveParts.messages[0];
          return null;
        });
        setReturnedSerialList([...serialList]);
      }
    });
  };

  /**
   * Common code for calling the API to move a part. If a revert operation is being done we switch the source and
   * destination of the call.
   * @param serialId
   * @param isRevert
   */
  const moveSinglePart = (serialId: string, isRevert: boolean): void => {
    const serial = serialId || currentSerial;
    const requestSource =
      sourceType.value === 'user_custody' ? source : sourceMobilityId.toString();
    const requestDestination = destType.value === 'user_custody' ? dest : destMobilityId.toString();
    const requestSourceType =
      sourceType.value === 'user_custody'
        ? 'user_custody'
        : sourceContainer?.containerDefinition.containerType;
    const requestDestinationType =
      destType.value === 'user_custody'
        ? 'user_custody'
        : destContainer?.containerDefinition.containerType;
    const parentContainers: MoveContainer[] = [];
    const parentMobilityIds = isRevert ? sourceParentMobilityIds : destParentMobilityIds;
    parentMobilityIds.forEach((container) => {
      parentContainers.push({ id: container.mobilityId, type: container.type });
    });
    movePart({
      variables: {
        serialId: serial,
        source: {
          id: isRevert ? requestDestination : requestSource,
          type: isRevert ? requestDestinationType : requestSourceType,
        },
        destination: {
          id: isRevert ? requestSource : requestDestination,
          type: isRevert ? requestSourceType : requestDestinationType,
        },
        parentContainers,
        login: username,
        workflowId: selectedWorkflow.value,
      },
    }).then((r) => {
      if (r.data.movePart.status === 'SUCCESS') {
        setReturnedSerialList([
          {
            response: isRevert ? 'reverted' : 'success',
            serialId: serial,
            location: isRevert ? source : dest,
            action: 'None',
          },
          ...returnedSerialList,
        ]);
      } else {
        setReturnedSerialList([
          {
            response: 'error',
            serialId: serial,
            location: isRevert ? dest : source,
            action: r.data.movePart.messages[0],
          },
          ...returnedSerialList,
        ]);
      }
    });
  };

  /**
   * This function handles the normal scanning operation and calls the API. It will first check if the serail id has
   * already been scanned and will not allow a duplicate scan if the serial has already been moved. This also checks
   * the parts we know are in the source bin and displays a warning in the table if the part is logically in a different
   * source.
   */
  const addNewSerialId = (): void => {
    const duplicateId = serialList.some((part) => {
      return part.serialId === currentSerial && ['success', 'reverted'].includes(part.response);
    });
    const serial = currentSerial;
    if (duplicateId) {
      setScanError(`Serial id ${currentSerial} has already been added.`);
    } else {
      // check if the serial is in the source
      const isPartInSource = bulkPartList.some((part) => {
        return part.serialId === currentSerial;
      });
      setCurrentSerial('');
      if (!isPartInSource) {
        setSerialList([
          {
            response: 'warning',
            serialId: serial,
            location: '-',
            action: `Serial was not found in ${source} are you sure this is the correct part?`,
            confirmFunction: confirmMove,
          },
          ...serialList,
        ]);
      } else {
        // do part move
        setCurrentSerial('');
        setSerialList([
          {
            response: 'loading',
            serialId: serial,
            location: '-',
            action: '-',
          },
          ...serialList,
        ]);
        moveSinglePart(serial, false);
      }
    }
  };

  /**
   * This will update the part in the table to be loading and then call the API to revert. Once the API returns a
   * a response, the same table record is updated to reverted or the appropriate error.
   */
  const revertPartMove = (): void => {
    setReturnedSerialList([
      {
        response: 'loading',
        serialId: selectedItems[0].serialId,
        location: '-',
        action: '-',
      },
      ...returnedSerialList,
    ]);
    moveSinglePart(selectedItems[0].serialId, true);
    setSelectedItems([]);
    setModalVisible(false);
  };

  const confirmMove = (serialId: string): void => {
    setReturnedSerialList([
      {
        response: 'loading',
        serialId,
        location: '-',
        action: '-',
      },
      ...returnedSerialList,
    ]);
    moveSinglePart(serialId, false);
  };

  const startOver = (): void => {
    setSourceType({ value: 'container', label: 'Container' });
    setDestType({ value: 'container', label: 'Container' });
    setSourceDisabled(false);
    setDestDisabled(false);
    setValidSource(ContainerValidated.UNCHECKED);
    setValidDest(ContainerValidated.UNCHECKED);
    setMoveAllChecked(false);
    setScanningStarted(false);
    setPartScanButtonDisabled(false);
    setContainerFieldsDisabled(false);
    setBulkMoveDisabled(false);
    setSerialList([]);
    setSelectedWorkflow({ value: '', label: 'Default' });
  };

  return (
    <SpaceBetween size="l">
      <BreadcrumbGroup items={breadcrumbs} />
      <TextContent>
        <h1>Parts Move</h1>
      </TextContent>
      <SpaceBetween size="m">
        <Container>
          <Grid
            gridDefinition={[
              { colspan: 4 },
              { colspan: 4 },
              { colspan: 3 },
              { colspan: 4 },
              { colspan: 4 },
              { colspan: 3 },
            ]}
          >
            <FormField label="Source type">
              <Select
                disabled={containerFieldsDisabled}
                onChange={({ detail }): void => {
                  setSourceType(detail.selectedOption);
                }}
                selectedOption={sourceType}
                placeholder="Select source type"
                /* When WW launches just add the concatenated value to ToaLifeCycleTypeOptionsV3 */
                options={[
                  { value: 'container', label: 'Container' },
                  { value: 'user_custody', label: 'User custody' },
                ]}
              />
            </FormField>
            <FormField label="Destination type">
              <Select
                disabled={containerFieldsDisabled}
                onChange={({ detail }): void => {
                  setDestType(detail.selectedOption);
                  setDestInputLabel(
                    detail.selectedOption.value === 'container' ? 'Container Tag' : 'User custody'
                  );
                }}
                selectedOption={destType}
                placeholder="Select source type"
                /* When WW launches just add the concatenated value to ToaLifeCycleTypeOptionsV3 */
                options={[
                  { value: 'container', label: 'Container' },
                  { value: 'user_custody', label: 'User custody' },
                ]}
              />
            </FormField>
            <div />
            <ValidatingContainerInput
              containerId={source}
              setContainerId={setSource}
              label={sourceInputLabel}
              disabled={sourceDisabled}
              validationStatus={validSource}
              setValidation={setValidSource}
              setContainer={setSourceContainer}
              setMobilityId={setSourceMobilityId}
              setParentMobilityIds={setSourceParentMobilityIds}
            />

            <ValidatingContainerInput
              containerId={dest}
              setContainerId={setDest}
              label={destInputLabel}
              disabled={destDisabled}
              validationStatus={validDest}
              setValidation={setValidDest}
              setContainer={setDestContainer}
              setMobilityId={setDestMobilityId}
              setParentMobilityIds={setDestParentMobilityIds}
            />
            <FormField label="Workflow">
              <Select
                disabled={containerFieldsDisabled}
                onChange={({ detail }): void => {
                  setSelectedWorkflow(detail.selectedOption);
                }}
                selectedOption={selectedWorkflow}
                options={workflowIds}
              />
            </FormField>
          </Grid>
          <Grid gridDefinition={[{ colspan: 2 }, { colspan: 2 }, { colspan: 4 }]}>
            <FormField label="&nbsp;">
              <Button
                disabled={partScanButtonDisabled}
                onClick={(): void => {
                  startScanningProcess();
                }}
              >
                Start scanning
              </Button>
            </FormField>
            <FormField label="&nbsp;">
              <Checkbox
                disabled={partsCheckboxDisabled}
                onChange={addAllPartsToMove}
                checked={moveAllChecked}
                className="awsui-visual-refresh"
              >
                Add all parts
              </Checkbox>
            </FormField>
            <FormField label="&nbsp;" errorText={errorText}>
              <div />
            </FormField>
          </Grid>
        </Container>
        {scanningStarted && (
          <SpaceBetween size="m">
            <Table
              header={
                <Header
                  actions={
                    <SpaceBetween direction="horizontal" size="xs">
                      <Button
                        disabled={isRevertDisabled}
                        onClick={(): void => setModalVisible(true)}
                      >
                        Revert
                      </Button>
                    </SpaceBetween>
                  }
                >
                  <FormField errorText={scanError}>
                    <RefScanInput
                      value={currentSerial}
                      autoFocus
                      onScan={(): void => {
                        addNewSerialId();
                      }}
                      onInput={({ detail }): void => setCurrentSerial(detail.value)}
                      placeholder="Scan Serial ID"
                      disabled={partScanDisabled}
                    />
                  </FormField>
                </Header>
              }
              onSelectionChange={({ detail }): void => setSelectedItems(detail.selectedItems)}
              selectionType="single"
              selectedItems={selectedItems}
              items={items}
              columnDefinitions={PART_SCAN_COLUMNS}
              isItemDisabled={(item): boolean => item.response !== 'success'}
              pagination={<Pagination {...paginationProps} />}
              empty={
                <div>
                  <Box variant="strong" textAlign="center" color="inherit">
                    No parts added
                  </Box>
                  <Box variant="p" padding={{ bottom: 's' }} color="inherit">
                    Start scanning serial IDs to move parts.
                  </Box>
                </div>
              }
            />
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button onClick={startOver}>Start over</Button>
                {moveAllChecked && (
                  <Button
                    variant="primary"
                    onClick={submitBulkPartsMove}
                    disabled={bulkMoveDisabled}
                  >
                    Move {serialList.length} parts
                  </Button>
                )}
              </SpaceBetween>
            </Box>
          </SpaceBetween>
        )}
      </SpaceBetween>
      <Modal
        className="awsui-visual-refresh"
        size="medium"
        visible={modalVisible}
        onDismiss={(): void => setModalVisible(false)}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={(): void => setModalVisible(false)}>
                Cancel
              </Button>
              <Button onClick={revertPartMove} disabled={confirmMoveDisabled} variant="primary">
                Confirm Move
              </Button>
            </SpaceBetween>
          </Box>
        }
        header="Confirm revert"
      >
        <TextContent>
          Confirm you want to move the following part back to {source}?
          <br />
          <br />
          Serial id: {selectedSerialsString}
        </TextContent>
      </Modal>
    </SpaceBetween>
  );
};

export default PartsMove;
